import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import gql from 'graphql-tag';
import moment from 'moment';
import { Cheque } from '@baemingo/table-events/dist/types';

import { useTableUniverse } from '~Components/TableUniverse';
import { useSelector } from '~Hooks';

import {
  getTables,
  getTablesVariables,
  searchOrders,
  searchOrdersCount,
  searchOrdersVariables,
  searchOrders_searchOrders_results as searchOrder,
} from './__queries__';
import OrderListView from './view';
import { useStorage } from '~Components/Storage';

const GET_TABLES = gql`
  query getTables($id: ID!) {
    venue: Venue(id: $id) {
      id
      tables {
        id
        code
      }
    }
  }
`;

const simplifiedOrderFragment = gql`
  fragment SimplifiedOrderFragment on ActiveOrder {
    id
    easyId
    createdAt
    status
    isTableOrder
    basketToken
    hasPrintedReceiptCopy
    paymentAmount
    paymentTip
    origin
  }
`;

const SEARCH_ORDERS = gql`
  query searchOrders(
    $venueIds: [String!]!
    $companyId: String!
    $startDate: String
    $endDate: String
    $sum: Int
    $easyId: String
    $deviceId: String
    $first: Int
    $skip: Int
  ) {
    searchOrders(
      venueIds: $venueIds
      companyId: $companyId
      startDate: $startDate
      endDate: $endDate
      sum: $sum
      easyId: $easyId
      deviceId: $deviceId
      first: $first
      skip: $skip
    ) {
      results {
        ...SimplifiedOrderFragment
      }
    }
  }

  ${simplifiedOrderFragment}
`;

const SEARCH_ORDERS_COUNT = gql`
  query searchOrdersCount(
    $venueIds: [String!]!
    $companyId: String!
    $startDate: String
    $endDate: String
    $sum: Int
    $easyId: String
    $deviceId: String
    $first: Int
    $skip: Int
  ) {
    searchOrders(
      venueIds: $venueIds
      companyId: $companyId
      startDate: $startDate
      endDate: $endDate
      sum: $sum
      easyId: $easyId
      deviceId: $deviceId
      first: $first
      skip: $skip
    ) {
      count
    }
  }
`;

export interface ChequeWithTableCode extends Cheque {
  tableCode?: string;
}

interface Props {
  companyId: string;
  venueId: string;
  selectedCheque: Cheque | undefined;
  setSelectedCheque: (cheque: Cheque | undefined) => void;
  selectedOrderId: string | null;
  setSelectedOrderId: (id: string | null) => void;
}

const OrderList = ({
  companyId,
  venueId,
  selectedCheque,
  setSelectedCheque,
  selectedOrderId,
  setSelectedOrderId,
}: Props) => {
  // Search
  const [searchText, setSearchText] = useState<string | undefined>(undefined);
  const [searchBySum, setSearchBySum] = useState<number | undefined>(undefined);
  const [chosenDate, setChosenDate] = useState<string | null>(null);
  const [page, setPage] = useState<number>(0);

  const [storage, updateStorage] = useStorage();

  const ORDERS_PER_PAGE = 125;
  const searchVariables: searchOrdersVariables = {
    venueIds: [venueId],
    companyId: companyId,
    first: ORDERS_PER_PAGE,
    skip: page * ORDERS_PER_PAGE,
    easyId: searchText,
    sum: searchBySum,
    deviceId: storage.filterOrderHistoryByDevice
      ? storage.taxComplianceDeviceId || undefined
      : undefined,
  };

  if (chosenDate) {
    searchVariables.startDate = moment(chosenDate)
      .hours(5)
      .minutes(30)
      .toISOString();
    searchVariables.endDate = moment(chosenDate)
      .add(1, 'days')
      .hours(5)
      .minutes(30)
      .toISOString();
  } else {
    // Default to past 7 days.
    searchVariables.startDate = moment()
      .subtract(7, 'days')
      .startOf('day')
      .toISOString();
    searchVariables.endDate = moment()
      .endOf('day')
      .toISOString();
  }

  // In progress orders
  const universe = useTableUniverse();
  const getTablesResult = useQuery<getTables, getTablesVariables>(GET_TABLES, {
    variables: { id: venueId },
  });
  const tables = getTablesResult.data?.venue.tables;

  const availableCheques: ChequeWithTableCode[] = useMemo(() => {
    let cheques: ChequeWithTableCode[] = universe.cheques.filter((c) =>
      universe.payments?.some((p) => p.chequeId === c.id),
    );

    if (tables) {
      cheques = cheques.map((c) => {
        // Make object extensible
        c = { ...c };
        c.tableCode = tables.find((t) => t.id === c.tableId)?.code;
        return c;
      });

      if (searchVariables.easyId) {
        cheques = cheques.filter((c) => c.tableCode === searchVariables.easyId);
      }
    }

    if (searchVariables.sum) {
      cheques = cheques.filter((c) => c.paidAmount === searchVariables.sum);
    }

    return cheques;
  }, [searchVariables, tables]);

  // Search for orders
  const [orders, setOrders] = useState<searchOrder[]>([]);
  const { data, loading } = useQuery<searchOrders>(SEARCH_ORDERS, {
    fetchPolicy: 'no-cache',
    variables: {
      ...searchVariables,
    },
  });

  // Number of results. This is slooooooow
  const countResult = useQuery<searchOrdersCount>(SEARCH_ORDERS_COUNT, {
    fetchPolicy: 'no-cache',
    variables: {
      ...searchVariables,
    },
  });

  useEffect(() => {
    if (loading) {
      return;
    }

    if (data) {
      setOrders(data?.searchOrders.results);
    }
  }, [data]);

  const numberOfPages =
    countResult.data && Math.ceil(countResult.data.searchOrders.count / ORDERS_PER_PAGE);

  const nextPage = () => {
    setPage((curPage) => curPage + 1);
  };

  const previousPage = () => {
    setPage((curPage) => curPage - 1);
  };

  // If user changes search params, reset pagination
  useEffect(() => {
    setPage(0);
  }, [searchText, searchBySum, chosenDate, venueId]);

  return (
    <OrderListView
      loading={loading}
      selectedCheque={selectedCheque}
      setSelectedCheque={setSelectedCheque}
      selectedOrderId={selectedOrderId}
      setSelectedOrderId={setSelectedOrderId}
      availableCheques={availableCheques}
      availableOrders={orders}
      setSearchText={setSearchText}
      setSearchBySum={setSearchBySum}
      chosenDate={chosenDate}
      setChosenDate={setChosenDate}
      currentPage={page + 1}
      numberOfPages={numberOfPages}
      nextPage={nextPage}
      previousPage={previousPage}
      showFilterByDevice={!!storage.taxComplianceDeviceId}
      filterOrderHistoryByDevice={storage.filterOrderHistoryByDevice}
      updateStorage={updateStorage}
    />
  );
};

export default OrderList;
